import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MenuProps } from "../types";
import {
  megaMenuPanel,
  megaMenuGroupTitle,
  megaMenuItemContainer,
  stylesText,
  activeRouteStyles,
  popperTriggerHover,
  overflowSubHoverText
} from "../styles";
import MoreLineIcon from "@/nav-localized/assets/header/nav/more-line.svg?react";
import ArrowIcon from "@/nav-localized/assets/header/arrow.svg?react";
import clsx from "clsx";
import { useTranslations } from "use-intl";
import { useStoreState } from "../store/useStore";
import { Languages, LanguagesType } from "../../types";

const CLOSE_DELAY = 80;

interface OverflowMenuProps {
  items: MenuProps[];
  changeRouter: (item: MenuProps, e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * "···" 溢出菜单组件
 *
 * 当 nav 项溢出可用宽度时，收起的项展示在此下拉菜单中。
 * hover 时图标变为主色，同时菜单下拉展开。
 * 展开面板采用 H5 手风琴布局：点击一级菜单展开/折叠子菜单。
 */
const OverflowMenu: FC<OverflowMenuProps> = ({ items, changeRouter }) => {
  const { theme, localeLink, locale } = useStoreState();
  const isRTL = locale === "ar";
  const t = useTranslations("header");
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [expandedLabels, setExpandedLabels] = useState<Set<string>>(new Set());

  const handleMouseEnter = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setExpandedLabels(new Set());
    }, CLOSE_DELAY);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const toggleExpanded = useCallback((label: string) => {
    setExpandedLabels((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }, []);

  const childRoute = (url: string, basePath: string) => {
    return `/${basePath}${localeLink}/${url}`;
  };

  const buildHref = (item: MenuProps) => {
    if (item.outbound) return item.url ?? "#";
    if (item.basePath) return childRoute(item.url!, item.basePath);
    return `${localeLink}/${item.url}`;
  };

  const getLabel = (label: string) => {
    return (
      Languages[locale as LanguagesType]?.header?.[
        label as keyof (typeof Languages)[LanguagesType]["header"]
      ] || label
    );
  };

  const [panelLeft, setPanelLeft] = useState<number | null>(null);

  const checkPosition = useCallback(() => {
    if (!containerRef.current || !dropdownRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const dropdownWidth = dropdownRef.current.offsetWidth || 0;
    const VIEWPORT_GUTTER = 16;
    const isRTL = getComputedStyle(containerRef.current).direction === "rtl";

    // LTR 默认左对齐，RTL 默认右对齐（面板右边缘对齐容器右边缘）
    let desiredLeft = isRTL ? containerRect.right - dropdownWidth : containerRect.left;

    // 约束到视口安全区域
    const minLeft = VIEWPORT_GUTTER;
    const maxLeft = window.innerWidth - VIEWPORT_GUTTER - dropdownWidth;
    desiredLeft = Math.max(minLeft, Math.min(maxLeft, desiredLeft));

    // 转换为相对于容器的 CSS left 偏移
    setPanelLeft(desiredLeft - containerRect.left);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", checkPosition);
    return () => window.removeEventListener("resize", checkPosition);
  }, [checkPosition]);

  useLayoutEffect(() => {
    if (!open) return;
    checkPosition();
    const raf = window.requestAnimationFrame(() => checkPosition());
    return () => window.cancelAnimationFrame(raf);
  }, [open, checkPosition]);

  return (
    <div
      ref={containerRef}
      className="flex items-center relative ml-[24px] h-[56px]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        className={clsx(
          "h-[56px] flex items-center cursor-pointer transition-colors duration-150",
          open ? activeRouteStyles({ mode: theme }) : stylesText({ mode: theme }),
          !open && popperTriggerHover({ mode: theme })
        )}
      >
        <MoreLineIcon className="w-[24px] h-[24px]" />
      </span>

      <div
        ref={dropdownRef}
        style={{
          ...(panelLeft !== null ? { left: panelLeft } : { [isRTL ? "left" : "right"]: 0 }),
          transform: open ? "translateY(4px)" : "translateY(0)"
        }}
        className={clsx(
          megaMenuPanel({ mode: theme }),
          "w-[360px] max-h-[70vh] overflow-y-auto",
          open
            ? "visible opacity-100 pointer-events-auto"
            : "invisible opacity-0 pointer-events-none"
        )}
      >
        <div className="p-[12px]">
          {items.map((item) => {
            const hasChildren = !!(item.items || item.more);
            const isExpanded = expandedLabels.has(item.label);

            if (!hasChildren) {
              return (
                <a
                  key={item.label}
                  href={`${item.url}`}
                  onClick={(e) => changeRouter(item, e)}
                  className={megaMenuItemContainer({ mode: theme })}
                >
                  {item.icon && (
                    <item.icon
                      className={clsx("w-[24px] h-[24px] shrink-0", stylesText({ mode: theme }))}
                    />
                  )}
                  <span
                    className={clsx(
                      "text-[14px] font-medium truncate",
                      stylesText({ mode: theme })
                    )}
                  >
                    {getLabel(item.label)}
                  </span>
                </a>
              );
            }

            return (
              <div key={item.label}>
                {/* 一级菜单：icon + label + arrow，点击展开/折叠 */}
                <div
                  className={megaMenuItemContainer({ mode: theme })}
                  onClick={() => toggleExpanded(item.label)}
                >
                  {item.icon && (
                    <item.icon
                      className={clsx("w-[24px] h-[24px] shrink-0", stylesText({ mode: theme }))}
                    />
                  )}
                  <span
                    className={clsx(
                      "text-[14px] font-medium whitespace-nowrap flex-1",
                      stylesText({ mode: theme })
                    )}
                  >
                    {getLabel(item.label)}
                  </span>
                  <ArrowIcon
                    className={clsx(
                      "w-4 h-4 transition-transform duration-200 shrink-0",
                      isExpanded && "rotate-180",
                      stylesText({ mode: theme })
                    )}
                  />
                </div>

                {/* 二级子菜单：手风琴展开 */}
                {isExpanded && (
                  <div className="flex flex-col gap-[8px] pl-[48px]">
                    {item.more
                      ? (() => {
                          const columns = Array.isArray(item.more)
                            ? item.more
                            : [item.more as { [key: string]: MenuProps[] }];

                          return columns.map((column, colIdx) =>
                            Object.entries(column).map(([groupKey, groupItems]) => {
                              if (!Array.isArray(groupItems) || groupItems.length === 0)
                                return null;
                              if (groupKey === "__widget__") return null;

                              const showGroupTitle = !groupKey.startsWith("__");
                              return (
                                <div key={`${colIdx}-${groupKey}`}>
                                  {showGroupTitle && (
                                    <div
                                      className={clsx(
                                        megaMenuGroupTitle({ mode: theme }),
                                        "!pl-0 !pr-[12px] !pt-[12px] !pb-[4px] !text-[12px]"
                                      )}
                                    >
                                      {t(groupKey)}
                                    </div>
                                  )}
                                  {groupItems.map((subItem, idx) => (
                                    <a
                                      href={buildHref(subItem)}
                                      key={`${colIdx}-${idx}`}
                                      className="group/sub"
                                      onClick={(e) => {
                                        window?.reportTrack?.("HeadingBar_Click", {
                                          tab_name: subItem.label
                                        });
                                        if (subItem.clickFunc) {
                                          subItem.clickFunc(e);
                                        } else {
                                          changeRouter(subItem, e);
                                        }
                                      }}
                                    >
                                      <div className="flex items-center py-[8px] pr-[12px] cursor-pointer transition-colors duration-150">
                                        <span
                                          className={clsx(
                                            "text-[14px] leading-[20px] truncate transition-colors duration-150",
                                            stylesText({ mode: theme }),
                                            overflowSubHoverText({ mode: theme })
                                          )}
                                        >
                                          {t(subItem.label)}
                                        </span>
                                      </div>
                                    </a>
                                  ))}
                                </div>
                              );
                            })
                          );
                        })()
                      : item.items?.map((subItem, idx) => (
                          <a
                            href={buildHref(subItem)}
                            key={idx}
                            className="group/sub"
                            onClick={(e) => {
                              window?.reportTrack?.("HeadingBar_Click", {
                                tab_name: subItem.label
                              });
                              if (subItem.clickFunc) {
                                subItem.clickFunc(e);
                              } else {
                                changeRouter(subItem, e);
                              }
                            }}
                          >
                            <div className="flex items-center py-[8px] pr-[12px] cursor-pointer transition-colors duration-150">
                              <span
                                className={clsx(
                                  "text-[14px] leading-[20px] truncate transition-colors duration-150",
                                  stylesText({ mode: theme }),
                                  overflowSubHoverText({ mode: theme })
                                )}
                              >
                                {t(subItem.label)}
                              </span>
                            </div>
                          </a>
                        ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OverflowMenu;
